/**
 * The module that validates common test criteria.
 * @module abt-core/validate
 */

import _ from 'lodash'
import cookies from '../abt-utils/cookies'
import siteConfig from './site-config.json'
import * as device from '../abt-utils/device'
import * as logger from '../abt-utils/logger'
import * as pageData from '../abt-utils/page-data'
import * as qa from './qa'
import * as queryString from '../abt-utils/query-string'

/**
 * Validates all the test criteria.
 * This will return early if we detect the test is disabled and not in QA.
 *
 * @param {object} test - The current test object being evaluated.
 * @returns {boolean} TRUE if all validations succeed, otherwise FALSE.
 */
export function all (test, opts) {
  var silent = opts && opts.silent
  var errors = []

  if (!_enabled(test)) {
    !silent && logger.warn('ABT: ' + test.id + ' disabled')
    return false
  }

  if (!_immune(test)) {
    errors = errors.concat(_inTestBuckets(test))
    errors = errors.concat(_inDateRange(test))
  }

  // validate criteria props against page data
  _.forOwn(test.pageCriteria, function (value, prop) {
    errors = errors.concat(_pageData(prop, value))
  })
  errors = errors.concat(device.validate(test))
  errors = errors.concat(_recipe(test))

  if (errors.length && !silent) {
    logger.warn('ABT: ' + test.id + ' removed due to', errors)
  }

  return !errors.length
}

/**
 * Determines if the test is enabled.
 * If QA is enabled, the test must be in QA in order to qualify as enabled.
 *
 * @param {object} test - The current test object being evaluated.
 * @returns {boolean} TRUE if the test is enabled, otherwise FALSE.
 */
function _enabled (test) {
  return (test.enabled && !qa.enabled()) || qa.includes(test)
}

/**
 * Determines if the client should be included in the test.
 *
 * This will check if the client's test bucket is included in the set of valid buckets provided
 * by the test. When a test does not define a set of valid buckets, it is assumed that all
 * buckets are valid and all clients should be inculded.
 *
 * @param {object} test - The current test object being evaluated.
 * @returns {boolean} TRUE if the client is included in the test, otherwise FALSE.
 */
function _inTestBuckets (test) {
  if (!test.buckets) {
    return []
  }

  var clientBucket = cookies.get(siteConfig.cookies.bucketCookieName)

  return _.includes(test.buckets, clientBucket) ? [] : ['bucket']
}

/**
 * Determines if the test's date range is valid.
 *
 * First checks that date ranges are specified correctly.
 * Then, checks the current date/time against each date range.
 * Time and GMT offset are expected to be set in the test's start and end dates.
 *
 * @param {object} test - The current test object being evaluated.
 * @returns {boolean} - TRUE if it is currently between the given date/times, otherwise FALSE
 */
export function _inDateRange (test) {
  var now = new Date()

  function withCurrentDate (dates) {
    var startDate = new Date(dates.start)
    var endDate = new Date(dates.end)
    return (now >= startDate && now <= endDate)
  }

  return _.find(test.dateRange, withCurrentDate) ? [] : ['date']
}

/**
 * Validates the value of any metadata property on the page.
 *
 * Checks to make sure the metadata property contains at least one of the values in the array.
 *
 * @param {string} prop - The property to validate.
 * @param {array} value - The array of valid values.
 * @returns {array} Empty if validation succeeds, otherwise contains the property that failed.
 */
export function _pageData (prop, value) {
  return pageData.contains(prop, value) ? [] : [prop]
}

/**
 * Determines if the test is immune to criteria validation.
 *
 * When a test is scaled or in QA (with QA enabled), it is considered immune.
 * Immunity is used to determine if certain criteria validation should be skipped.
 *
 * @param {object} test - The current test object being evaluated.
 * @returns {boolean} TRUE if the test is immune, otherwise FALSE.
 */
function _immune (test) {
  return qa.includes(test) || test.type === siteConfig.testType.SCALED
}

/**
 * Determines if the assigned recipe is valid.
 * @param {object} test - The test object to validate.
 * @returns {boolean} TRUE if the recipe is unassigned or valid, otherwise FALSE.
 */
function _recipe (test) {
  var assigned = queryString.get(test.id) || cookies.get(test.id)
  if (!assigned) {
    return []
  }

  var view = device.getSize()
  // TODO: in lodash 3.1.10, this can be changed to use dot notation
  return (test[view] && _.has(test[view], assigned)) ? [] : ['recipe invalid']
}



/*****************
 ** WEBPACK FOOTER
 ** ./src/abt-core/validate.js
 ** module id = 11
 ** module chunks = 2 3
 **/